import { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    CircularProgress,
    Chip,
    alpha
} from '@mui/material';
import { useSelector } from 'react-redux';
import HistoryIcon from '@mui/icons-material/History';
import PersonIcon from '@mui/icons-material/Person';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { $authHost } from '../../../http/index.js';

// Словарь перевода названий полей
const fieldTranslations = {
    'host.name': 'Имя хоста',
    'host.host': 'Техническое имя',
    'host.status': 'Статус',
    'host.description': 'Описание',
    'host.proxy_hostid': 'Прокси',
    'host.ipmi_authtype': 'Тип авторизации IPMI',
    'host.ipmi_privilege': 'Привилегии IPMI',
    'host.ipmi_username': 'Пользователь IPMI',
    'host.ipmi_password': 'Пароль IPMI',
    'host.tls_connect': 'TLS подключение',
    'host.tls_accept': 'TLS приём',
    'host.tls_issuer': 'TLS издатель',
    'host.tls_subject': 'TLS субъект',
    'host.tls_psk_identity': 'TLS PSK идентификатор',
    'host.tls_psk': 'TLS PSK ключ',
    'host.inventory_mode': 'Режим инвентаризации',
    'host.inventory': 'Инвентарь',
    'hostinventory.location': 'Расположение',
    'hostinventory.location_lat': 'Широта',
    'hostinventory.location_lon': 'Долгота',
    'hostinventory.name': 'Название',
    'hostinventory.type': 'Тип',
    'hostinventory.os': 'ОС',
    'hostinventory.serialno_a': 'Серийный номер A',
    'hostinventory.serialno_b': 'Серийный номер B',
    'hostinventory.asset_tag': 'Инвентарный номер',
    'hostinventory.macaddress_a': 'MAC адрес A',
    'hostinventory.macaddress_b': 'MAC адрес B',
    'hostinventory.hardware': 'Оборудование',
    'hostinventory.software': 'ПО',
    'hostinventory.contact': 'Контакт',
    'hostinventory.notes': 'Заметки',
    'hostinventory.url_a': 'URL A',
    'hostinventory.url_b': 'URL B',
    'hostinventory.url_c': 'URL C',
    'host.interfaces': 'Интерфейсы',
    'hostinterface.ip': 'IP адрес',
    'hostinterface.dns': 'DNS',
    'hostinterface.port': 'Порт',
    'hostinterface.type': 'Тип интерфейса',
    'hostinterface.main': 'Основной',
    'hostinterface.useip': 'Использовать IP',
    'host.groups': 'Группы хостов',
    'hostgroup.name': 'Имя группы',
    'host.templates': 'Шаблоны',
    'template.name': 'Имя шаблона',
    'host.macros': 'Макросы',
    'usermacro.macro': 'Макрос',
    'usermacro.value': 'Значение макроса',
    'host.tags': 'Теги',
    'tag.tag': 'Тег',
    'tag.value': 'Значение тега',
    'trigger.description': 'Описание триггера',
    'trigger.expression': 'Выражение',
    'trigger.priority': 'Приоритет',
    'trigger.status': 'Статус триггера',
    'item.name': 'Имя элемента',
    'item.key_': 'Ключ элемента',
    'item.delay': 'Интервал',
    'item.history': 'История',
    'item.trends': 'Тренды',
    'item.status': 'Статус элемента',
    'item.units': 'Единицы измерения',
    'user.name': 'Имя пользователя',
    'user.username': 'Логин',
    'user.surname': 'Фамилия',
    'user.autologin': 'Автовход',
    'user.autologout': 'Автовыход',
    'user.lang': 'Язык',
    'user.theme': 'Тема',
    'user.type': 'Тип пользователя',
    'user.medias': 'Способы оповещения',
    'user.usrgrps': 'Группы пользователя',
    'host.monitored_by': 'Мониторить через',
    'host.proxyid': 'Прокси ID'
};

const excludedFields = [
    'host.monitored_by', 'host.proxyid', 'host.proxy_hostid', 'host.hostid',
    'host.templateid', 'hostinterface.interfaceid', 'hostinterface.hostid',
    'hostgroup.groupid', 'template.templateid', 'usermacro.hostmacroid', 'tag.hosttagid'
];

const isFieldExcluded = (field) => {
    const cleanField = field.replace(/\[\d+\]/g, '');
    return excludedFields.some(excluded => 
        cleanField === excluded || cleanField.endsWith('.' + excluded.split('.').pop())
    );
};

const translateField = (field) => {
    const cleanField = field.replace(/\[\d+\]/g, '');
    if (fieldTranslations[cleanField]) return fieldTranslations[cleanField];
    const parts = cleanField.split('.');
    for (let i = parts.length - 1; i >= 0; i--) {
        const partialKey = parts.slice(i).join('.');
        if (fieldTranslations[partialKey]) return fieldTranslations[partialKey];
    }
    return field;
};

const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Н/Д';
    const date = new Date(parseInt(timestamp) * 1000);
    return date.toLocaleString('ru-RU', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
    });
};

// Компонент для отображения одной записи изменения
const ChangeEntry = ({ field, action, oldValue, newValue, timestamp, username, isGeneralEvent, isDarkMode }) => {
    const actionTypes = {
        0: { label: 'Добавление', icon: AddCircleIcon, color: isDarkMode ? '#3fb950' : '#2da44e' },
        1: { label: 'Изменение', icon: EditIcon, color: isDarkMode ? '#58a6ff' : '#0969da' },
        2: { label: 'Удаление', icon: DeleteIcon, color: '#d63a23' },
        4: { label: 'Выход', icon: PersonIcon, color: 'var(--text-muted)' },
        5: { label: 'Выполнение', icon: HistoryIcon, color: '#f5bb3a' }
    };

    const actionInfo = actionTypes[action] || actionTypes[1];
    const ActionIcon = actionInfo.icon;
    const translatedField = isGeneralEvent ? field : translateField(field);
    
    const formatValue = (val) => {
        if (val === undefined || val === null || val === '') {
            return <em style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>пусто</em>;
        }
        return String(val);
    };

    return (
        <Box
            sx={{
                p: { xs: 0.75, md: 1 },
                mb: { xs: 0.5, md: 0.75 },
                backgroundColor: alpha(actionInfo.color, 0.06),
                border: `1px solid ${alpha(actionInfo.color, 0.2)}`,
                borderLeft: (theme) => ({ xs: `2px solid ${actionInfo.color}`, md: `3px solid ${actionInfo.color}` }),
                borderRadius: { xs: '6px', md: '8px' },
                transition: 'all 0.15s ease',
                '&:hover': {
                    backgroundColor: alpha(actionInfo.color, 0.1)
                }
            }}
        >
            {/* Заголовок */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, md: 0.75 }, mb: isGeneralEvent ? 0 : { xs: 0.5, md: 0.75 }, flexWrap: 'wrap' }}>
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: { xs: 20, md: 22 },
                    height: { xs: 20, md: 22 },
                    borderRadius: { xs: '4px', md: '5px' },
                    backgroundColor: actionInfo.color,
                    color: 'white',
                    flexShrink: 0
                }}>
                    <ActionIcon sx={{ fontSize: { xs: 10, md: 12 } }} />
                </Box>
                
                <Typography sx={{ fontWeight: 600, color: actionInfo.color, fontSize: { xs: '0.75rem', md: '0.8rem' }, flex: 1, minWidth: 0 }}>
                    {translatedField}
                </Typography>
                
                <Chip
                    label={actionInfo.label}
                    size="small"
                    sx={{
                        height: { xs: 16, md: 18 },
                        fontSize: { xs: '0.55rem', md: '0.6rem' },
                        fontWeight: 600,
                        backgroundColor: actionInfo.color,
                        color: 'white',
                        '& .MuiChip-label': { px: { xs: 0.5, md: 0.75 } }
                    }}
                />
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.4, md: 0.5 } }}>
                    <AccessTimeIcon sx={{ fontSize: { xs: 10, md: 11 }, color: 'var(--text-muted)' }} />
                    <Typography sx={{ fontSize: { xs: '0.6rem', md: '0.65rem' }, color: 'var(--text-muted)' }}>
                        {timestamp}
                    </Typography>
                </Box>
                
                {username && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.4, md: 0.5 } }}>
                        <PersonIcon sx={{ fontSize: { xs: 10, md: 11 }, color: 'var(--text-muted)' }} />
                        <Typography sx={{ fontSize: { xs: '0.6rem', md: '0.65rem' }, color: 'var(--text-muted)' }}>
                            {username}
                        </Typography>
                    </Box>
                )}
            </Box>
            
            {/* Для общих событий — название ресурса */}
            {isGeneralEvent && newValue && (
                <Box sx={{ pl: { xs: 2.5, md: 3.5 } }}>
                    <Typography sx={{ color: 'var(--text-secondary)', fontSize: { xs: '0.7rem', md: '0.75rem' } }}>
                        {newValue}
                    </Typography>
                </Box>
            )}
            
            {/* Значения: до → после */}
            {!isGeneralEvent && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.75, md: 1 }, pl: { xs: 2.5, md: 3.5 }, flexWrap: 'wrap' }}>
                    <Box sx={{ 
                        flex: '1 1 auto',
                        minWidth: { xs: '100%', md: 80 },
                        maxWidth: { xs: '100%', md: '42%' },
                        p: { xs: 0.5, md: 0.75 },
                        backgroundColor: alpha('#d63a23', 0.08),
                        borderRadius: { xs: '4px', md: '6px' },
                        border: `1px solid ${alpha('#d63a23', 0.2)}`
                    }}>
                        <Typography sx={{ 
                            fontSize: { xs: '0.5rem', md: '0.55rem' }, 
                            fontWeight: 600,
                            color: '#d63a23',
                            textTransform: 'uppercase',
                            mb: 0.25
                        }}>
                            До
                        </Typography>
                        <Typography sx={{ fontSize: { xs: '0.7rem', md: '0.75rem' }, color: '#d63a23', wordBreak: 'break-word' }}>
                            {formatValue(oldValue)}
                        </Typography>
                    </Box>
                    
                    <ArrowForwardIcon sx={{ color: actionInfo.color, fontSize: { xs: 14, md: 16 }, flexShrink: 0, display: { xs: 'none', md: 'block' } }} />
                    
                    <Box sx={{ 
                        flex: '1 1 auto',
                        minWidth: { xs: '100%', md: 80 },
                        maxWidth: { xs: '100%', md: '42%' },
                        p: { xs: 0.5, md: 0.75 },
                        backgroundColor: alpha(isDarkMode ? '#3fb950' : '#2da44e', 0.08),
                        borderRadius: { xs: '4px', md: '6px' },
                        border: `1px solid ${alpha(isDarkMode ? '#3fb950' : '#2da44e', 0.2)}`
                    }}>
                        <Typography sx={{ 
                            fontSize: { xs: '0.5rem', md: '0.55rem' }, 
                            fontWeight: 600,
                            color: isDarkMode ? '#3fb950' : '#2da44e',
                            textTransform: 'uppercase',
                            mb: 0.25
                        }}>
                            После
                        </Typography>
                        <Typography sx={{ fontSize: { xs: '0.7rem', md: '0.75rem' }, color: isDarkMode ? '#3fb950' : '#2da44e', wordBreak: 'break-word' }}>
                            {formatValue(newValue)}
                        </Typography>
                    </Box>
                </Box>
            )}
        </Box>
    );
};

const ComplexAuditLog = props => {
    const { host, hostType = 'default' } = props;
    const isDarkMode = useSelector(state => state.settings.darkMode);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [changes, setChanges] = useState([]);

    const fetchAuditLog = async (hostid) => {
        try {
            const { data } = await $authHost.get(`/api/state/id/${hostid}/audit-log?limit=100`);
            if (data.success) return data.data;
            return [];
        } catch (err) {
            console.error(`Ошибка загрузки журнала для хоста ${hostid}:`, err);
            throw err;
        }
    };

    const parseAuditEntries = (entries, hostName) => {
        const allChanges = [];
        
        entries.forEach(entry => {
            const timestamp = formatTimestamp(entry.clock);
            const username = entry.username || 'Система';
            const actionType = parseInt(entry.action);
            
            let details = null;
            try {
                if (entry.details && entry.details.trim() !== '') {
                    details = typeof entry.details === 'string' ? JSON.parse(entry.details) : entry.details;
                }
            } catch (e) {
                details = null;
            }
            
            if (!details || typeof details !== 'object' || Object.keys(details).length === 0) {
                const actionLabels = { 0: 'Создан узел', 1: 'Изменён узел', 2: 'Удалён узел' };
                allChanges.push({
                    field: actionLabels[actionType] || 'Событие',
                    action: actionType,
                    oldValue: null,
                    newValue: entry.resourcename || hostName,
                    timestamp, username, hostName,
                    clock: entry.clock,
                    isGeneralEvent: true
                });
                return;
            }
            
            Object.keys(details).forEach(field => {
                if (isFieldExcluded(field)) return;
                
                const value = details[field];
                let oldValue = null;
                let newValue = null;
                
                if (Array.isArray(value)) {
                    if (value.length === 3) { newValue = value[1]; oldValue = value[2]; }
                    else if (value.length === 2) { newValue = value[0]; oldValue = value[1]; }
                    else if (value.length === 1) { newValue = value[0]; }
                } else {
                    newValue = value;
                }
                
                allChanges.push({ field, action: actionType, oldValue, newValue, timestamp, username, hostName, clock: entry.clock });
            });
        });
        
        return allChanges;
    };

    useEffect(() => {
        const loadAuditLogs = async () => {
            setLoading(true);
            setError(null);

            try {
                let allChanges = [];
                const mainEntries = await fetchAuditLog(host.hostid);
                const mainChanges = parseAuditEntries(mainEntries, host.name);
                allChanges.push(...mainChanges);

                if (hostType === 'crossroad' && host.child && host.child.length > 0) {
                    const childPromises = host.child.map(async (childHost) => {
                        try {
                            const childEntries = await fetchAuditLog(childHost.hostid);
                            const childName = childHost.name.split('/')[1] || childHost.name;
                            return parseAuditEntries(childEntries, childName);
                        } catch { return []; }
                    });
                    const childResults = await Promise.all(childPromises);
                    childResults.forEach(changes => allChanges.push(...changes));
                }

                allChanges.sort((a, b) => parseInt(b.clock) - parseInt(a.clock));
                setChanges(allChanges);
            } catch (err) {
                console.error('Ошибка загрузки журнала аудита:', err);
                setError('Не удалось загрузить журнал аудита');
            } finally {
                setLoading(false);
            }
        };

        if (host?.hostid) loadAuditLogs();
    }, [host, hostType]);

    // Загрузка
    if (loading) {
        return (
            <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column',
                alignItems: 'center', 
                justifyContent: 'center',
                height: '100%',
                gap: { xs: 1.5, md: 2 },
                backgroundColor: 'var(--background-default)'
            }}>
                <CircularProgress size={{ xs: 28, md: 36 }} sx={{ color: 'var(--primary-color)' }} />
                <Typography sx={{ fontSize: { xs: '0.75rem', md: '0.85rem' }, color: 'var(--text-muted)' }}>
                    Загрузка журнала аудита...
                </Typography>
            </Box>
        );
    }

    // Ошибка
    if (error) {
        return (
            <Box sx={{ 
                p: { xs: 1.5, md: 2 }, 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                height: '100%',
                backgroundColor: 'var(--background-default)'
            }}>
                <Paper
                    elevation={0}
                    sx={{
                        p: { xs: 2, md: 4 },
                        textAlign: 'center',
                        backgroundColor: 'var(--background-paper)',
                        borderRadius: { xs: '8px', md: '12px' },
                        border: '1px solid var(--border-default)',
                        maxWidth: { xs: '100%', md: 400 }
                    }}
                >
                    <WarningAmberIcon sx={{ fontSize: { xs: 40, md: 56 }, color: '#f5bb3a', mb: { xs: 1.5, md: 2 }, opacity: 0.7 }} />
                    <Typography sx={{ fontSize: { xs: '0.875rem', md: '1rem' }, fontWeight: 500, color: 'var(--text-primary)', mb: 0.5 }}>
                        Журнал аудита недоступен
                    </Typography>
                    <Typography sx={{ fontSize: { xs: '0.75rem', md: '0.85rem' }, color: 'var(--text-muted)', mb: { xs: 1, md: 1.5 } }}>
                        Для просмотра требуются права Супер-администратора
                    </Typography>
                    <Box sx={{
                        p: { xs: 0.75, md: 1 },
                        backgroundColor: alpha('#f5bb3a', 0.08),
                        border: `1px solid ${alpha('#f5bb3a', 0.3)}`,
                        borderRadius: { xs: '6px', md: '8px' }
                    }}>
                        <Typography sx={{ fontSize: { xs: '0.75rem', md: '0.8rem' }, color: '#f5bb3a' }}>
                            {error}
                        </Typography>
                    </Box>
                </Paper>
            </Box>
        );
    }

    // Пусто
    if (changes.length === 0) {
        return (
            <Box sx={{ 
                p: { xs: 1.5, md: 2 }, 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                height: '100%',
                backgroundColor: 'var(--background-default)'
            }}>
                <Paper
                    elevation={0}
                    sx={{
                        p: { xs: 2, md: 4 },
                        textAlign: 'center',
                        backgroundColor: 'var(--background-paper)',
                        borderRadius: { xs: '8px', md: '12px' },
                        border: '1px solid var(--border-default)'
                    }}
                >
                    <HistoryIcon sx={{ fontSize: { xs: 40, md: 56 }, color: 'var(--text-muted)', mb: { xs: 1.5, md: 2 }, opacity: 0.5 }} />
                    <Typography sx={{ fontSize: { xs: '0.875rem', md: '1rem' }, fontWeight: 500, color: 'var(--text-primary)', mb: 0.5 }}>
                        Записей не найдено
                    </Typography>
                    <Typography sx={{ fontSize: { xs: '0.75rem', md: '0.85rem' }, color: 'var(--text-muted)' }}>
                        Журнал аудита для данного узла пуст
                    </Typography>
                </Paper>
            </Box>
        );
    }

    return (
        <Box sx={{ 
            p: { xs: 1, md: 1.5 }, 
            width: '100%', 
            height: '100%', 
            overflow: 'auto',
            backgroundColor: 'var(--background-default)',
            '&::-webkit-scrollbar': { width: { xs: '4px', md: '6px' } },
            '&::-webkit-scrollbar-track': { background: 'var(--background-subtle)', borderRadius: '3px' },
            '&::-webkit-scrollbar-thumb': { 
                background: 'var(--border-default)', 
                borderRadius: '3px',
                '&:hover': { background: 'var(--text-muted)' }
            }
        }}>
            {/* Заголовок */}
            <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: { xs: 1, md: 1.5 }, 
                mb: { xs: 1, md: 1.5 },
                p: { xs: 0.75, md: 1 },
                backgroundColor: 'var(--background-paper)',
                borderRadius: { xs: '8px', md: '10px' },
                border: '1px solid var(--border-default)',
                flexWrap: { xs: 'wrap', md: 'nowrap' }
            }}>
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: { xs: 28, md: 32 },
                    height: { xs: 28, md: 32 },
                    borderRadius: { xs: '6px', md: '8px' },
                    backgroundColor: isDarkMode ? '#58a6ff' : '#0969da',
                    color: 'white',
                    flexShrink: 0
                }}>
                    <HistoryIcon sx={{ fontSize: { xs: 16, md: 18 } }} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: { xs: '0.8rem', md: '0.9rem' }, fontWeight: 600, color: 'var(--text-primary)' }}>
                        Журнал аудита
                    </Typography>
                    <Typography sx={{ fontSize: { xs: '0.65rem', md: '0.7rem' }, color: 'var(--text-muted)' }}>
                        {changes.length} {changes.length === 1 ? 'изменение' : changes.length < 5 ? 'изменения' : 'изменений'}
                        {hostType === 'crossroad' && host.child && ` • ${host.child.length + 1} узлов`}
                    </Typography>
                </Box>
            </Box>

            {/* Записи */}
            <Box>
                {changes.map((change, index) => (
                    <ChangeEntry 
                        key={`${change.field}-${change.clock}-${index}`}
                        field={change.field}
                        action={change.action}
                        oldValue={change.oldValue}
                        newValue={change.newValue}
                        timestamp={change.timestamp}
                        username={change.username}
                        isGeneralEvent={change.isGeneralEvent}
                        isDarkMode={isDarkMode}
                    />
                ))}
            </Box>
        </Box>
    );
};

export default ComplexAuditLog;
